import React, { useState, useEffect, useRef } from 'react'
import { Clock } from 'lucide-react'

interface ReservationTimerProps {
  expiresAt: string
  onExpire: () => void
}

/**
 * RESERVATION COUNTDOWN TIMER
 *
 * Displays a live countdown timer showing how much time remains on the user's
 * cart reservation. Changes color to red when < 1 minute remains.
 *
 * Props:
 * - expiresAt: ISO timestamp when reservation expires
 * - onExpire: Callback fired when timer reaches 00:00
 */
const ReservationTimer: React.FC<ReservationTimerProps> = ({ expiresAt, onExpire }) => {
  const [remaining, setRemaining] = useState(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    // Calculate initial remaining time
    const calculateRemaining = () => {
      const diff = Math.max(0, new Date(expiresAt).getTime() - Date.now())
      return diff
    }

    // Set initial time
    setRemaining(calculateRemaining())

    // Update every second
    const interval = setInterval(() => {
      const diff = calculateRemaining()

      if (isMountedRef.current) {
        setRemaining(diff)
      }

      // Fire onExpire callback when time runs out
      if (diff === 0) {
        clearInterval(interval)
        if (isMountedRef.current) {
          onExpire()
        }
      }
    }, 1000)

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false
      clearInterval(interval)
    }
  }, [expiresAt, onExpire])

  // Format time as MM:SS
  const minutes = String(Math.floor((remaining / 1000 / 60) % 60)).padStart(2, '0')
  const seconds = String(Math.floor((remaining / 1000) % 60)).padStart(2, '0')

  // Show urgent styling when < 1 minute remains
  const isUrgent = remaining < 60000

  return (
    <div className={`bg-maroon-800 text-white rounded-lg shadow-lg p-4 mb-6 ${isUrgent ? 'animate-pulse' : ''}`}>
      <div className="flex flex-col items-center text-center gap-2">
        <div className="flex items-center justify-center gap-2">
          <Clock size={20} className="text-white shrink-0" />
          <span className="font-semibold">Your order is reserved for</span>
        </div>
        <span className="font-mono font-bold text-xl bg-white text-maroon-800 px-3 py-1 rounded leading-none">
          {minutes}:{seconds}
        </span>
      </div>

      {isUrgent && (
        <div className="mt-2 text-sm font-medium text-center text-white">
          Complete your payment soon to secure your order!
        </div>
      )}
    </div>
  )
}

export default ReservationTimer
